/**
 * Merci : la date de livraison de la ligne de réassurance.
 *
 * Les règles du client, pour une commande passée à l'heure de Paris — nouveau
 * brief du 2026-10-05, qui remplace celui du 2026-09-24 (J+1 et J+2) :
 *
 *                                  période creuse     période forte
 *   lundi → jeudi, avant 11 h      J+3 ouvrés         J+4 ouvrés
 *   lundi → jeudi, dès 11 h        J+4 ouvrés         J+5 ouvrés
 *   vendredi                       mercredi suivant   jeudi suivant
 *   samedi, dimanche               jeudi suivant      vendredi suivant
 *
 * Ces dix cas n'en font que trois. Le vendredi est un J+3 **quelle que soit
 * l'heure** — c'est la seule exception à la limite de 11 h. Un jour non ouvré
 * se compte comme le matin du jour ouvré suivant : du lundi, J+3 donne bien le
 * jeudi. Et la période forte ajoute un jour ouvré partout, ce qui donne
 * exactement la seconde colonne. Le nouveau brief n'a changé que le délai de
 * base, `DELAI_OUVRES` : la structure des règles est restée la même.
 *
 * Le client parle d'expédition ; la page parle de **livraison**, décision de
 * Gabriel le 2026-09-24 — c'est ce que les clientes veulent lire. Les dates
 * sont celles des règles, seul le mot change.
 *
 * --- Les jours fériés comptent comme des dimanches
 *
 * Décision de Gabriel le 2026-09-24. Concrètement, deux effets :
 *
 *   - un férié n'est **jamais** un jour ouvré, donc on le saute en comptant ;
 *   - une commande passée **un** férié se traite comme une commande du
 *     dimanche : comptée au matin du prochain jour ouvré, puis J+1.
 *
 * Exemple : l'Ascension tombe le jeudi 14 mai 2026. Une commande du mercredi 13
 * après 11 h donne J+4 ouvrés — le 14 est sauté, restent le vendredi 15, le
 * lundi 18, le mardi 19 et le mercredi 20.
 *
 * Ce sont les onze fériés légaux de métropole, calculés et non saisis : huit à
 * date fixe, trois qui suivent Pâques. Pas de liste à tenir à jour, donc pas
 * d'année oubliée. Le lundi de Pentecôte en fait partie : il est férié en droit,
 * même là où il sert de journée de solidarité.
 *
 * --- Pourquoi en JavaScript et non en Liquid
 *
 * Parce que Shopify **met les pages en cache**. Une date calculée par Liquid
 * est celle du moment où la page a été rendue, qui peut remonter à plusieurs
 * heures — et autour de 11 h, c'est tout l'enjeu. Le navigateur, lui, connaît
 * l'heure au moment où l'on lit.
 *
 * --- Pourquoi l'heure de Paris et non celle du visiteur
 *
 * Parce que la règle suit l'horloge de l'entrepôt. Un client à Montréal qui
 * commande à 7 h est déjà à 13 h à Paris : il a manqué la limite de 11 h, et sa
 * date doit le dire.
 */

/** La limite horaire, à Paris. Au-delà, la commande part un jour plus tard. */
const HEURE_LIMITE = 11;

/** Le délai de base en jours ouvrés, avant la limite horaire et hors période forte. */
const DELAI_OUVRES = 3;

const JOUR = 24 * 60 * 60 * 1000;

/**
 * Le dimanche de Pâques d'une année, en calendrier grégorien.
 *
 * Algorithme de Meeus, Jones et Butcher : exact pour toute année grégorienne,
 * sans table.
 *
 * @param {number} annee
 * @returns {number} Midi UTC du dimanche de Pâques.
 */
function paques(annee) {
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31);
  const jour = ((h + l - 7 * m + 114) % 31) + 1;

  return Date.UTC(annee, mois - 1, jour, 12);
}

/** @type {Map<number, Set<string>>} Les fériés déjà calculés, par année. */
const feriesParAnnee = new Map();

/**
 * Les fériés légaux de métropole pour une année, en dates ISO.
 *
 * @param {number} annee
 * @returns {Set<string>}
 */
function feries(annee) {
  let liste = feriesParAnnee.get(annee);
  if (liste) return liste;

  const iso = (instant) => new Date(instant).toISOString().slice(0, 10);
  const dimanchePaques = paques(annee);

  liste = new Set([
    `${annee}-01-01`, // Jour de l'an
    iso(dimanchePaques + 1 * JOUR), // Lundi de Pâques
    `${annee}-05-01`, // Fête du Travail
    `${annee}-05-08`, // Victoire 1945
    iso(dimanchePaques + 39 * JOUR), // Ascension
    iso(dimanchePaques + 50 * JOUR), // Lundi de Pentecôte
    `${annee}-07-14`, // Fête nationale
    `${annee}-08-15`, // Assomption
    `${annee}-11-01`, // Toussaint
    `${annee}-11-11`, // Armistice 1918
    `${annee}-12-25`, // Noël
  ]);

  feriesParAnnee.set(annee, liste);
  return liste;
}

/**
 * Un jour ouvré : ni samedi, ni dimanche, ni férié.
 *
 * @param {number} instant Midi UTC du jour à tester.
 */
function estOuvre(instant) {
  const date = new Date(instant);
  const jour = date.getUTCDay();
  if (jour === 0 || jour === 6) return false;
  return !feries(date.getUTCFullYear()).has(date.toISOString().slice(0, 10));
}

/**
 * Le calendrier et l'heure de Paris à un instant donné.
 *
 * `Intl` et non un décalage fixe : Paris passe de UTC+1 à UTC+2 deux fois par
 * an, et un `+1` écrit en dur se tromperait d'une heure sept mois sur douze —
 * précisément autour de la limite de 11 h.
 *
 * @param {Date} instant
 */
function aParis(instant) {
  const morceaux = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(instant)
      .map((morceau) => [morceau.type, morceau.value])
  );

  return {
    annee: Number(morceaux.year),
    mois: Number(morceaux.month),
    jour: Number(morceaux.day),
    heure: Number(morceaux.hour),
  };
}

class MerciDeliveryDate extends HTMLElement {
  #minuterie;
  #dernier;

  /**
   * La règle elle-même, sans rien toucher au document — d'où sa place en
   * méthode statique : on peut l'appeler sur n'importe quel jour et n'importe
   * quelle heure pour la vérifier.
   *
   * Le jour de la semaine se déduit de la date plutôt que de se passer à part :
   * deux informations qui doivent concorder sont deux façons de se tromper.
   *
   * @param {{ annee: number, mois: number, jour: number, heure: number }} commande
   *   Le moment de la commande, à Paris.
   * @param {boolean} forte La période forte.
   * @returns {Date} La date de livraison, à midi UTC.
   */
  static calculer(commande, forte) {
    // Midi UTC plutôt que minuit : les additions de 24 h qui suivent ne
    // risquent pas de basculer sur la veille ou le lendemain à cause d'un
    // changement d'heure.
    let date = Date.UTC(commande.annee, commande.mois - 1, commande.jour, 12);
    let restants;

    if (!estOuvre(date)) {
      // Week-end ou férié : compté comme le matin du prochain jour ouvré.
      do date += JOUR;
      while (!estOuvre(date));
      restants = DELAI_OUVRES;
    } else if (new Date(date).getUTCDay() === 5) {
      // Le vendredi ignore la limite horaire.
      restants = DELAI_OUVRES;
    } else {
      restants = commande.heure < HEURE_LIMITE ? DELAI_OUVRES : DELAI_OUVRES + 1;
    }

    if (forte) restants += 1;

    while (restants > 0) {
      date += JOUR;
      if (estOuvre(date)) restants -= 1;
    }

    return new Date(date);
  }

  /** Les fériés retenus pour une année, pour vérification. */
  static feries(annee) {
    return [...feries(annee)].sort();
  }

  connectedCallback() {
    this.#rendre();

    // La page peut rester ouverte au-delà de 11 h, ou de minuit. Un contrôle
    // par minute suffit à ne jamais annoncer une date que la commande ne
    // tiendrait plus ; il ne réécrit rien tant que la date ne change pas.
    this.#minuterie = setInterval(() => this.#rendre(), 60 * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#minuterie);
  }

  #rendre() {
    const date = MerciDeliveryDate.calculer(aParis(new Date()), this.dataset.peak === 'true');
    const iso = date.toISOString().slice(0, 10);
    if (iso === this.#dernier) return;
    this.#dernier = iso;

    // Dans la langue de la page, comme la phrase qui l'entoure : celle-ci vient
    // des fichiers de traduction du thème.
    const libelle = new Intl.DateTimeFormat(document.documentElement.lang || 'fr', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(date);

    // La phrase se découpe autour de `[date]`. Écrite en nœuds et non en
    // `innerHTML` : ce qu'on saisit dans l'administration reste du texte,
    // quoi qu'on y mette.
    const morceaux = (this.dataset.template || '[date]').split('[date]');
    const fragment = document.createDocumentFragment();

    for (const [rang, morceau] of morceaux.entries()) {
      if (rang > 0) {
        const element = document.createElement('time');
        element.dateTime = iso;
        element.textContent = libelle;
        fragment.append(element);
      }
      if (morceau) fragment.append(morceau);
    }

    this.replaceChildren(fragment);
  }
}

if (!customElements.get('merci-delivery-date')) {
  customElements.define('merci-delivery-date', MerciDeliveryDate);
}
